<template>
  <v-layout class="email-create-container">
    <v-layout wrap class="form-wrapper">
      <v-layout wrap>
        <v-flex xs12>
          <div v-if="$apollo.loading">
            <spinner />
          </div>
          <form v-scroll-to-error @submit.prevent="submitForm">
            <v-flex class="title-screen">
              <span>{{ adminText('organizationAll.create_e_mail_b5580838', 'Create e-mail') }}</span>
            </v-flex>
            <v-flex x4>
              <div class="title-description screen-description" />
            </v-flex>
            <v-flex>
              <create-sport-subscription-email-form
                :organization-slug="organizationSlug"
                :vs="$v"
                :form="form"
                @values="form = $event"
                @shortCodeHandle="handleEventShorCodesRender($event)"
              />
            </v-flex>
            <div class="email-shortcodes">
              <email-shortcodes :shortcodes="shortcodes" @shortcode="shortcodeAdded" />
            </div>
            <div class="buttons-container">
              <main-button flat @click.prevent="goBack"> {{ adminText('organizationAll.cancel_1507c40f', 'CANCEL') }}</main-button>
              <div class="lower-buttons">
                <main-button depressed @click="$emit('submit')"> {{ adminText('organizationAll.send_a2469c47', 'SEND') }}</main-button>
              </div>
            </div>
          </form>
        </v-flex>
      </v-layout>
    </v-layout>
    <div class="email-shortcodes desktop-shortcodes">
      <email-shortcodes :shortcodes="shortcodes" @shortcode="shortcodeAdded" />
    </div>
    <success-snackbar
      :open.sync="wasTestEmailSent"
      :title="adminText('organizationAll.success_42a8f651', 'Success')"
      :message="adminText('organizationAll.your_test_email_was_sent_successfully_to_the__96f44288', 'Your test email was sent successfully to the organizer!')"
    />
    <error-snackbar
      :open="emailError"
      :title="adminText('organizationAll.oops_c8e633f4', 'Oops')"
      :message="adminText('organizationAll.an_error_occurred_while_trying_to_send_your_e_adbb9de8', 'An error occurred while trying to send your email.')"
    />
  </v-layout>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { email, required } from 'vuelidate/lib/validators'
import EmailShortcodes from '@/components/emails/email-shortcodes.vue'
import SuccessSnackbar from '@/components/shared/snackbars/success-snackbar.vue'
import ErrorSnackbar from '@/components/shared/snackbars/errors-snackbar.vue'
import { GET_ORGANIZATION } from '@/graphql/organization.graphql'
import { CREATE_ORGANIZATION_EMAIL, SEND_ORGANIZATION_EMAIL } from '@/graphql/email.graphql'
import { submitGuard } from '@/utils'
import CreateSportSubscriptionEmailForm from '@/components/emails/CreateSportSubscriptionEmailForm.vue'
import { isEmpty } from 'lodash'

export default {
  mixins: [OrganizationLocalization],
  name: 'sport-subscription-create-email',
  components: {
    CreateSportSubscriptionEmailForm,
    EmailShortcodes,
    SuccessSnackbar,
    ErrorSnackbar,
  },
  props: {
    organizationSlug: String,
    initialValues: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    const additionalInitialValues = !isEmpty(this.initialValues)
      ? {
        subscriptionPlan: this.initialValues.prepaidCoupon.id,
        recipients: 'Specific subscribers',
        attendees: this.initialValues.attendees.map(item => item.id.toString()),
      }
      : {}

    return {
      form: {
        content: '',
        replyTo: '',
        ...additionalInitialValues,
      },
      wasTestEmailSent: false,
      emailError: false,
      shortcodes: [
        'Subscription name',
        'Subscription start date',
        'Subscription end date',
        'Price',
        'Subscriber name',
        'Subscriber email',
      ],
    }
  },
  validations() {
    return {
      form: {
        replyTo: { required, email },
        recipients: { required },
        subject: { required },
        content: {
          required,
          requiredShortCodes: () => this.handleTextEditorErrorMsg(),
        },
      },
    }
  },
  apollo: {
    getOrganization: {
      query: GET_ORGANIZATION,
      manual: true,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading) {
          const {
            getOrganization: { email },
          } = data
          this.form.replyTo = email
        }
      },
    },
  },
  methods: {
    handleTextEditorErrorMsg() {
      const val = this.form.content

      return !(
        (val.includes('{ Confirm }') || val.includes('{ Reject }')) &&
        !val.includes('{ Subscriber name }')
      )
    },
    filterSubscribersWithEmail(prepaidCouponUsers) {
      return prepaidCouponUsers.filter(prepaidCouponUser => !!prepaidCouponUser.fields['Email'])
    },
    handleEventShorCodesRender() {
      if (this.form.eventId) {
        this.shortcodes.push('Confirm')
        this.shortcodes.push('Reject')
      } else {
        this.shortcodes = this.shortcodes.filter(sc => sc !== 'Confirm' && sc !== 'Reject')
        this.form.content = this.form.content.replace('{ Confirm }', '')
        this.form.content = this.form.content.replace('{ Reject }', '')
      }
    },
    async shortcodeAdded(shortcode) {
      if (this.form.content) {
        this.form.content = this.form.content
          .slice(0, this.form.content.length - 4)
          .concat(` { ${shortcode} }`)
      } else {
        this.form.content = this.form.content.concat(`{ ${shortcode} }`)
      }
    },
    async createNewEmail() {
      const { replyTo, eventId, recipients, subject, content, attendees } = this.form
      const identifiers = attendees.length ? attendees.map(a => a.id || a) : []
      const email = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_EMAIL,
        variables: {
          organizationSlug: this.organizationSlug,
          email: {
            eventId,
            replyTo: replyTo,
            recipientType: recipients,
            subject: subject,
            content: content,
            identifiers,
            meta: { prepaidCoupon: true },
          },
        },
      })
      const {
        data: {
          createOrganizationEmail: { id },
        },
      } = email
      const response = await this.$apollo.mutate({
        mutation: SEND_ORGANIZATION_EMAIL,
        variables: {
          organizationSlug: this.organizationSlug,
          emailId: id,
        },
      })
      const { errors } = response
      if (errors) {
        this.emailError = true
      } else {
        this.goTo(this.routes.sportOrganization.sportOrganizationSubscriptionsEmails, {
          wasEmailSent: true,
        })
      }
    },
    async submitForm() {
      await submitGuard(this, async () => {
        await this.createNewEmail()
      })
    },
  },
}
</script>

<style scoped>
.screen-description {
  max-width: 680px;
  margin-bottom: 28px;
}

.form-container {
  margin-right: 30px;
  max-width: 40.75rem;
}

.desktop-shortcodes {
  margin: 352px -60px 0 0;
  min-width: 200px !important;
  display: block !important;
  margin-left: 24px;
}

.email-shortcodes {
  display: none;
}

@media screen and (max-width: 1279px) {
  .desktop-shortcodes {
    display: none !important;
  }

  .email-shortcodes {
    margin: 16px 0;
    margin-bottom: 32px;
    display: block;
  }
}

.title-screen {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 1.5rem;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}

.form-wrapper {
  max-width: 40.75rem;
}

@media screen and (max-width: 800px) {
  .title-screen {
    display: none;
  }

  .form-wrapper {
    margin-top: 24px;
  }
}
</style>
